import { Link } from 'react-router';
import { AnswerCard, PromptCard } from '../components/Cards';
import { Footer, TopBar, useDocumentTitle } from '../components/Chrome';
import { SuggestForm } from '../components/SuggestForm';
import { Demo } from './marketing/Demo';

export function HowToPlay() {
  useDocumentTitle('How to play');
  return (
    <>
      <TopBar />
      <main className="page stack">
        <h1>How to play</h1>
        <p>
          CompleteMasti is a fill-in-the-blank party game for desi families and friends. Everyone plays on their own phone. You need at least 3 players,
          and it is best with 4 to 10.
        </p>

        <Demo />

        <section className="panel stack mk-60">
          <h2 style={{ margin: 0 }}>Your first game in 60 seconds</h2>
          <ol className="mk-60-steps">
            <li>
              <span className="mk-60-t">0:00</span>
              <span>
                Type your name on <Link to="/">the home page</Link> and tap <strong>Start a new game</strong>.
              </span>
            </li>
            <li>
              <span className="mk-60-t">0:10</span>
              <span>Tap the WhatsApp button in the lobby, or read the 4-letter code out loud.</span>
            </li>
            <li>
              <span className="mk-60-t">0:30</span>
              <span>Everyone opens the link on their phone and types a name. That's it, no app.</span>
            </li>
            <li>
              <span className="mk-60-t">0:50</span>
              <span>Once 3 or more people are in, the host taps start.</span>
            </li>
            <li>
              <span className="mk-60-t">1:00</span>
              <span>The judge reads the first prompt out loud. Play your funniest card.</span>
            </li>
          </ol>
          <Link className="btn btn-primary btn-block" to="/" data-cta="howto_60_start">
            Start the clock
          </Link>
        </section>

        <h2>The rules</h2>
        <ol className="steps">
          <li>
            <div>
              <strong>Start a room.</strong> One person taps "Start a new game" and shares the 4-letter code (or the WhatsApp invite). Everyone else joins
              from their phone.
            </div>
          </li>
          <li>
            <div>
              <strong>A prompt card comes up.</strong> Each round has a judge (the 👑) who reads the prompt out loud.
            </div>
          </li>
          <li>
            <div>
              <strong>Everyone else plays their funniest answer card.</strong> Some prompts say PICK 2: choose two cards, in order.
            </div>
          </li>
          <li>
            <div>
              <strong>The judge flips the cards one by one</strong>, reads each out loud, and crowns a winner. The winner gets a point and the judge seat moves on.
            </div>
          </li>
          <li>
            <div>
              <strong>First to the target score wins.</strong> Then play again, obviously.
            </div>
          </li>
        </ol>

        <div className="demo-cards" aria-hidden="true">
          <PromptCard card={{ id: 'h1', type: 'prompt', text: 'Mere paas ___ hai.', pick: 1, gloss: 'I have ___.', explanation: 'Shashi Kapoor\'s famous comeback in Deewaar (1975). The original answer: "Maa."' }} />
          <AnswerCard card={{ id: 'h2', type: 'answer', text: 'A Costco membership', pick: 1 }} />
        </div>

        <h2>Made for mixed crowds</h2>
        <ul>
          <li>
            <strong>Translations:</strong> Hindi and Urdu lines show a short English translation under the card. Turn them off from the ☰ menu if your crowd
            doesn't need them.
          </li>
          <li>
            <strong>The (i) button:</strong> tap it on any card to find out which film, song, person or custom it is about. No more pausing the game to
            explain who Mogambo is.
          </li>
          <li>
            <strong>Everyone votes mode:</strong> instead of a judge, everybody plays every round and votes for their favourite (you can't vote for your
            own). Great for big groups and video calls.
          </li>
          <li>
            <strong>Blank cards:</strong> everyone gets a couple of blank cards per game to write their own answer. You can also send a good one to us for
            the official deck.
          </li>
          <li>
            <strong>Hand swaps:</strong> stuck with a terrible hand? Trade the whole thing in (once per game by default).
          </li>
          <li>
            <strong>TV mode:</strong> open the TV link on a laptop or smart TV so the whole room sees the prompt, the card flips, and the winner.
          </li>
          <li>
            <strong>Your own packs:</strong> <Link to="/packs/new">make a pack</Link> of family inside jokes and add it to any game by its code.
          </li>
        </ul>

        <h2>Extra masti</h2>
        <p>
          Played a few games already? The host can switch these on in the lobby under <strong>Extra masti</strong>. Mix and match.
        </p>
        <ul>
          <li>
            <strong>Prompt choice:</strong> the judge gets two prompts and picks the one they like best. (Judge mode only.)
          </li>
          <li>
            <strong>Guess who:</strong> once the winning card is picked, everyone guesses who played it before the name is revealed. Guess right and you get
            a point too.
          </li>
          <li>
            <strong>Teams:</strong> split up, say Kids vs Parents. Every point you win goes to your team, and the first team to the target wins. In voting
            games, you can't vote for your own team's cards.
          </li>
          <li>
            <strong>Write your own prompt:</strong> every few rounds, the judge (or, in voting games, each player in turn) writes the prompt. Leave a blank
            (___) or ask a question. Stuck? Use a card from the deck.
          </li>
          <li>
            <strong>Sabse Bekaar rounds:</strong> every few rounds, play your worst card on purpose. The most bekaar answer wins the point.
          </li>
        </ul>

        <h2>More ways to play</h2>
        <ul>
          <li>
            <strong>Only one phone?</strong> <Link to="/games/one-phone">One-phone mode</Link> passes a single phone around the room.
          </li>
          <li>
            <strong>Living room games:</strong> <Link to="/games/charades">Dumb Charades</Link>, <Link to="/games/dialogue">Finish the Dialogue</Link> and{' '}
            <Link to="/games/mat-bolna">Mat Bolna</Link>, with teams, a timer and big text. See them all on the <Link to="/games">party games page</Link>.
          </li>
          <li>
            <strong>Real cards:</strong> prefer something to hold? We make <Link to="/shop">printed decks</Link>, including one of your own family pack.
          </li>
        </ul>

        <h2>Packs</h2>
        <p>
          <strong>Classic</strong> and <strong>Filmy Dialogues</strong> are on by default. <strong>Shaadi Season</strong> is for wedding season.{' '}
          <strong>Besharam</strong> is 18+ and stays off unless the host turns it on. See them all on the <Link to="/packs">packs page</Link>.
        </p>

        <h2>Good to know</h2>
        <ul>
          <li>If your phone locks or you refresh, just open the page again. You keep your seat, cards and score.</li>
          <li>If the judge disappears for 30 seconds, the round is reshuffled with a new judge.</li>
          <li>The host can remove players, hand over the host role, skip a round, or end the game from the ☰ menu.</li>
        </ul>
        <Link className="btn btn-primary btn-lg" to="/" data-cta="howto_bottom_start">
          Start playing
        </Link>
      </main>
      <Footer />
    </>
  );
}

export function SuggestPage() {
  useDocumentTitle('Suggest a card');
  return (
    <>
      <TopBar />
      <main className="page stack">
        <h1>Suggest a card</h1>
        <p className="muted">
          Got a line your family says every single time? A filmy dialogue we missed? Send it over. The good ones go into the official packs.
        </p>
        <div className="panel">
          <SuggestForm />
        </div>
      </main>
      <Footer />
    </>
  );
}

export function NotFound() {
  useDocumentTitle('Not found');
  return (
    <main className="page stack center" style={{ paddingTop: '18vh' }}>
      <img className="logo" src="/logo-mark.png" alt="" style={{ width: 110, margin: '0 auto' }} />
      <h1>Arre, this page doesn't exist</h1>
      <Link className="btn btn-primary btn-lg" to="/">
        Go home
      </Link>
    </main>
  );
}
